// src/pages/AgendaInvitePage.js
import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Alert } from 'react-bootstrap';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAgendaAuth } from '../context/AgendaAuthContext.js';

export default function AgendaInvitePage() {
  const navigate = useNavigate();
  const { token: urlToken } = useParams();
  const { register, isAuthenticated } = useAgendaAuth();

  const [formData, setFormData] = useState({
    email: '',
    name: '',
    whatsappNumber: '',
    password: '',
    confirmPassword: ''
  });

  const [loading, setLoading] = useState(false);
  const [validatingInvite, setValidatingInvite] = useState(true);
  const [inviteValid, setInviteValid] = useState(false);
  const [inviteToken, setInviteToken] = useState(null);
  const [inviteEmail, setInviteEmail] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const API_URL = process.env.REACT_APP_API_URL;

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/agenda');
    }
  }, [isAuthenticated, navigate]);

  // Validate invite token on mount
  useEffect(() => {
    const validateInvite = async () => {
      if (!urlToken) {
        setError('Link de convite invalido. Entre em contato com o administrador.');
        setValidatingInvite(false);
        setInviteValid(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/agenda/invites/validate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: urlToken })
        });

        const data = await response.json();

        if (data.success) {
          setInviteValid(true);
          setInviteToken(urlToken);
          if (data.invite.email) {
            setInviteEmail(data.invite.email);
            setFormData(prev => ({ ...prev, email: data.invite.email }));
          }
        } else {
          setError(data.error || 'Convite invalido ou expirado');
          setInviteValid(false);
        }
      } catch (err) {
        setError('Erro ao validar convite. Tente novamente.');
        setInviteValid(false);
      } finally {
        setValidatingInvite(false);
      }
    };

    validateInvite();
  }, [urlToken, API_URL]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validacoes
    if (!formData.email || !formData.password) {
      setError('Email e senha sao obrigatorios');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('As senhas nao coincidem');
      return;
    }

    if (formData.password.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres');
      return;
    }

    setLoading(true);

    const result = await register(
      formData.email,
      formData.password,
      formData.name,
      formData.whatsappNumber,
      inviteToken
    );

    setLoading(false);

    if (result.success) {
      setSuccess('Conta criada com sucesso! Redirecionando...');
      setTimeout(() => {
        navigate('/agenda');
      }, 1500);
    } else {
      setError(result.error || 'Falha no registro');
    }
  };

  // Loading while validating
  if (validatingInvite) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div className="spinner-border text-light" role="status">
          <span className="visually-hidden">Validando convite...</span>
        </div>
      </div>
    );
  }

  // Invalid invite
  if (!inviteValid) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)',
        display: 'flex',
        alignItems: 'center',
        padding: '20px'
      }}>
        <Container style={{ maxWidth: '450px' }}>
          <Card style={{ borderRadius: '15px', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
            <Card.Body style={{ padding: '40px', textAlign: 'center' }}>
              <h3 style={{ color: '#333', marginBottom: '20px' }}>Convite Invalido</h3>
              {error && <Alert variant="danger">{error}</Alert>}
              <p style={{ color: '#666', marginTop: '20px' }}>
                Entre em contato com o administrador para obter um novo convite.
              </p>
              <Link to="/agenda/login" style={{ color: '#007bff', fontWeight: '600' }}>
                Ja tem conta? Faca login
              </Link>
            </Card.Body>
          </Card>
        </Container>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)',
      display: 'flex',
      alignItems: 'center',
      padding: '20px'
    }}>
      <Container style={{ maxWidth: '450px' }}>
        <Card style={{ borderRadius: '15px', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
          <Card.Body style={{ padding: '40px' }}>
            {/* Logo */}
            <div style={{ textAlign: 'center', marginBottom: '30px' }}>
              <h1 style={{
                fontSize: '28px',
                fontWeight: '700',
                background: 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                marginBottom: '10px'
              }}>
                Agenda Pessoal
              </h1>
              <p style={{ color: '#666', fontSize: '14px' }}>
                It's So Clean
              </p>
            </div>

            <h4 style={{ textAlign: 'center', marginBottom: '25px', color: '#333' }}>
              Criar Conta
            </h4>

            {error && <Alert variant="danger">{error}</Alert>}
            {success && <Alert variant="success">{success}</Alert>}

            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  readOnly={!!inviteEmail}
                  style={{
                    padding: '12px',
                    fontSize: '15px',
                    backgroundColor: inviteEmail ? '#f0f0f0' : 'white'
                  }}
                />
                {inviteEmail && (
                  <Form.Text className="text-muted">
                    Este email esta especificado no seu convite
                  </Form.Text>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Nome (opcional)</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  style={{ padding: '12px', fontSize: '15px' }}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>WhatsApp (para lembretes)</Form.Label>
                <Form.Control
                  type="tel"
                  name="whatsappNumber"
                  value={formData.whatsappNumber}
                  onChange={handleChange}
                  style={{ padding: '12px', fontSize: '15px' }}
                />
                <Form.Text className="text-muted">
                  Formato: 64XXXXXXXXX (ex: 64275954995)
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Senha</Form.Label>
                <Form.Control
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  style={{ padding: '12px', fontSize: '15px' }}
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label>Confirmar Senha</Form.Label>
                <Form.Control
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  style={{ padding: '12px', fontSize: '15px' }}
                />
              </Form.Group>

              <Button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '14px',
                  fontSize: '16px',
                  fontWeight: '600',
                  background: 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)',
                  border: 'none',
                  borderRadius: '8px'
                }}
              >
                {loading ? 'Criando conta...' : 'Criar Conta'}
              </Button>
            </Form>

            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <p style={{ color: '#666' }}>
                Ja tem conta?{' '}
                <Link
                  to="/agenda/login"
                  style={{
                    color: '#007bff',
                    textDecoration: 'none',
                    fontWeight: '600'
                  }}
                >
                  Fazer Login
                </Link>
              </p>
            </div>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
}
